// 在vue中实现react context 用于往深层子孙组件传递非必要非业务参数，
// 例如埋点的obj_type(通常跟页面或者tab相关，对于跨页面通用组件需要一层一层往下传，也不能写在vuex之类的地方)
// 非必要，非业务的要求是因为，过度使用context会让组件复用度变低，难以维护
// 使用场景：子组件需要获得自身所处的环境信息，如商品卡片的埋点想要知道自己处于第几个tab的列表内
import Vue from 'vue';

const setEventKey = '$black_unique_set_context_event$';
// 组件实例使用这个字段名来读取context
const vmContextKey = 'context$';
// 要使用context，必须先在这里注册一个key，否则无法设置
const contextKeys = [
    'tjLocation',
    'searchKeyWord',
    'searchTypeName',
    'operationSideData', // src/blackcard/components/homeComponent/promotionCard/promotionCard.vue
];

/**
 * @type {Map<Vue,Map>}
 */
const contextMap = new Map();

/**
 *
 * @param {Vue} vm
 * @param {string} contextKey
 * @returns {[any,Vue]}
 */
function getContext(vm, contextKey) {
    const context = contextMap.get(vm.$parent);
    // 类原型链式的访问逻辑
    if (!context || !context.has(contextKey)) {
        if (!vm.$parent) return [null, vm];
        return getContext(vm.$parent, contextKey);
    }
    return [context.get(contextKey), vm.$parent];
}

/**
 * 声明当前组件需要使用的context
 * @param {Object} opt
 * @param {[string]} opt.useKeys 声明当前组件需要用到的context key
 * @param {Map} [opt.initSetContext] 设置默认的context，此值存在时，会使用这个对象初始化设置当前组件的context
 */
export default function useContext({ useKeys, initSetContext }) {
    return {
        data: () => ({ [vmContextKey]: {} }),
        created() {
            const context = this[vmContextKey];
            useKeys.forEach(key => {
                const [value, pvm] = getContext(this, key);
                Vue.set(context, key, value);
                pvm.$on(setEventKey, ({ key: contextKey, value }) => {
                    this[vmContextKey][contextKey] = value;
                });
            });
            if (initSetContext) {
                const initContext = { ...initSetContext };
                const keys = Object.keys(initContext).filter(key => !contextKeys.includes(key));
                if (keys.length)
                    throw new Error(`contextKey “${keys.join(',')}”未定义，请到src/common/util/context.js注册改key`);
                const thisContext = new Map(Object.entries(initContext));
                contextMap.set(this, thisContext);
            }
        },
    };
}

/**
 * 移除当前组件设置的context（无法操作父组件设置的context）
 * @param {Vue} vm
 * @param {string} contextKey
 */
export function delThisContext(vm, contextKey) {
    const context = contextMap.get(vm);
    if (!context) return;
    context.delete(contextKey);
}

/**
 * 获取当前组件设置的某个context
 * @param {Vue} vm
 * @param {string} contextKey
 */
export function getThisContext(vm, contextKey) {
    const context = contextMap.get(vm);
    if (!context) return null;
    return context.get(contextKey);
}

/**
 * 设置某个context的值，传递给子孙组件使用；
 * 设置完之后，当前组件获取到的依然是父组件设置的值，
 * 如果想在当前组件获取当前组件设置的值，请使用组件的data，或者使用getThisContext
 * @param {Vue} vm vue组件实例
 * @param {string} contextKey
 * @param {*} value context的值
 */
export function setContext(vm, contextKey, value) {
    if (!contextKeys.includes(contextKey))
        throw new Error(`contextKey “${contextKey}”未定义，请到src/common/util/context.js注册改key`);
    const context = contextMap.get(vm) || new Map();
    context.set(contextKey, value);
    contextMap.set(vm, context);
    vm.$emit(setEventKey, { key: contextKey, value });
}
